import { useQuery } from '@apollo/client';
import {
  SortingDirection,
  UserFilterInput,
  UsersDocument,
  UsersQuery,
} from 'src/graphql/generated/graphql';
import { useTable } from 'src/hooks/use-table';
import { UsersLookupTable } from './lookup-table';
import { UsersHistoryTable } from './history-table';
import { Typography } from '@otp/ui';
import { User } from '@otp/icons';
import { useTranslation } from 'react-i18next';

type UsersLookupListProps = {
  filter: UserFilterInput;
};

const PAGINATION_LIMIT = 10;

export const UsersLookupList = ({ filter }: UsersLookupListProps) => {
  const { t } = useTranslation();

  const {
    page,
    setPage,
    sorting,
    sortingField,
    sortingDirection,
    onSortChange,
  } = useTable<UserFilterInput, Record<string, SortingDirection>>({
    defaultSorting: {
      lastName: SortingDirection.Asc,
      firstName: SortingDirection.Asc,
    },
  });

  const { loading, data } = useQuery<UsersQuery>(UsersDocument, {
    variables: {
      filter,
      sorting,
      pagination: {
        limit: PAGINATION_LIMIT,
        offset: page * PAGINATION_LIMIT,
      },
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
  });

  const totalCount = data?.users?.pagination?.totalCount;
  const users = data?.users?.records || [];
  const userIds = users.map((user) => user.id);

  return (
    <>
      <Typography
        variant="subtitle1"
        className="mb-4 mt-4 flex items-center gap-2 font-bold"
      >
        <User /> {t('common.users')}
      </Typography>
      <UsersLookupTable
        data={users}
        loading={loading}
        options={{
          page,
          count: totalCount,
          sort: true,
          onColumnSortChange: onSortChange,
          rowsPerPage: PAGINATION_LIMIT,
          onChangePage: setPage,
          sortOrder: {
            name: sortingField as string,
            direction:
              sortingDirection === SortingDirection.Desc ? 'desc' : 'asc',
          },
        }}
      />

      {userIds.length > 0 && (
        <>
          <Typography
            variant="subtitle1"
            className="mb-4 mt-4 flex items-center gap-2 font-bold"
          >
            {t('common.history')}
          </Typography>
          <UsersHistoryTable userIds={userIds} />
        </>
      )}
    </>
  );
};
